import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ShoppingBag, Package, Wand2, Briefcase, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ quotes: 0, orders: 0, products: 0, jobs: 0, wizard: false });
  const isTech = user?.role === "technician";
  const isAdminOrOwner = user?.role === "admin" || user?.role === "owner";

  useEffect(() => {
    if (isTech) {
      api.get("/me/orders/assigned").then((r) => setStats((s) => ({ ...s, jobs: r.data.length }))).catch(() => {});
      return;
    }
    Promise.all([
      api.get("/quotes"),
      api.get("/orders"),
      api.get("/products"),
      user?.role === "owner" ? api.get("/settings/admin") : Promise.resolve({ data: { wizard_completed: true } }),
      api.get("/me/orders/assigned").catch(() => ({ data: [] })),
    ]).then(([q, o, p, s, j]) => {
      setStats({ quotes: q.data.length, orders: o.data.length, products: p.data.length, jobs: j.data.length, wizard: s.data.wizard_completed });
    }).catch(() => {});
  }, [isTech, user]);

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900">{isTech ? "Technician overview" : isAdminOrOwner ? "Owner overview" : "Overview"}</h1>
      <p className="mt-1 text-sm text-slate-500">
        {isTech ? "Your assigned jobs and field tools." : "Snapshot of your store + service requests."}
      </p>

      {!isTech && user?.role === "owner" && !stats.wizard && (
        <Link to="/dashboard/setup" className="mt-6 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 p-5 hover:bg-blue-100" data-testid="dashboard-setup-banner">
          <div className="flex items-center gap-3">
            <Wand2 className="h-5 w-5 text-blue-700" />
            <div>
              <div className="font-medium text-blue-900">Finish your setup wizard</div>
              <div className="text-sm text-blue-700/80">Configure payment options and notification recipients.</div>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-blue-700" />
        </Link>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Stat label="My jobs" value={stats.jobs} icon={Briefcase} to="/dashboard/jobs" testid="stat-jobs" />
        {isAdminOrOwner && <Stat label="Quote requests" value={stats.quotes} icon={Inbox} to="/dashboard/quotes" testid="stat-quotes" />}
        {isAdminOrOwner && <Stat label="Orders" value={stats.orders} icon={ShoppingBag} to="/dashboard/orders" testid="stat-orders" />}
        {isAdminOrOwner && <Stat label="Products" value={stats.products} icon={Package} to="/dashboard/products" testid="stat-products" />}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, to, testid }) {
  return (
    <Link to={to} className="dash-surface block p-5 hover:border-blue-300 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-slate-900">{value}</div>
    </Link>
  );
}
